import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import { AppContext } from "contexts/AppContext";
import { columnsData } from "views/admin/reservations/variables/columnsData";
import ReservationTable from "./components/ReservationTable";
import {
  createStaffReservation,
  deleteStaffReservation,
  getStaffReservations,
  updateStaffReservation,
  updateStaffReservationStatus,
} from "Api/apiService";

const STAFF_STATUS_OPTIONS = [
  "pending",
  "confirmed",
  "seated",
  "completed",
  "cancelled",
];

const STATUS_COLOR = {
  pending: "yellow",
  confirmed: "green",
  seated: "blue",
  completed: "purple",
  cancelled: "red",
};

const isKnownReservationStatus = status =>
  STAFF_STATUS_OPTIONS.includes(String(status || "").toLowerCase());

const toReservationState = reservation => {
  const raw = String(reservation?.status || "").toLowerCase();
  if (isKnownReservationStatus(raw)) {
    return raw;
  }
  const explicit = String(reservation?.reservationState || "").toLowerCase();
  if (isKnownReservationStatus(explicit)) {
    return explicit;
  }
  if (explicit === "reserved" || explicit === "confirmed") {
    return "confirmed";
  }
  if (explicit === "pending") {
    return "pending";
  }
  return "pending";
};

const toReservationStatusLabel = status => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "confirmed") return "Confirmed";
  if (normalized === "seated") return "Seated";
  if (normalized === "completed") return "Completed";
  if (normalized === "cancelled") return "Cancelled";
  return "Pending";
};

const toReservationCode = reservation =>
  String(reservation?.reservationCode || "").trim() ||
  `GRV-${String(reservation?._id || "").slice(-8).toUpperCase()}`;

export default function Reservation() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [staffReservations, setStaffReservations] = useState([]);
  const [reservationSearch, setReservationSearch] = useState("");
  const [loadingStaffReservations, setLoadingStaffReservations] = useState(false);
  const [savingReservationId, setSavingReservationId] = useState("");
  const [creatingReservation, setCreatingReservation] = useState(false);
  const [deletingReservationId, setDeletingReservationId] = useState("");
  const [savingEditReservation, setSavingEditReservation] = useState(false);
  const [pendingStatusById, setPendingStatusById] = useState({});
  const [editingReservationId, setEditingReservationId] = useState("");
  const [editReservation, setEditReservation] = useState({
    fullName: "",
    emailAddress: "",
    phoneNumber: "",
    guests: "2",
    date: new Date().toISOString().slice(0, 10),
    time: "7:00 PM",
    vibe: "",
    status: "pending",
    adminNotes: "",
  });
  const [newReservation, setNewReservation] = useState({
    fullName: "",
    emailAddress: "",
    phoneNumber: "",
    guests: "2",
    date: new Date().toISOString().slice(0, 10),
    time: "7:00 PM",
    vibe: "",
  });

  const options = [{ value: "1", label: "All Reservation Blocks" }];
  const [selectedOption, setSelectedOption] = useState("1");

  const { reservationData, fetchReservationData } = useContext(AppContext);

  useEffect(() => {
    fetchReservationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStaffReservations = async () => {
    setLoadingStaffReservations(true);
    try {
      const response = await getStaffReservations();
      const data = response.data?.reservations || [];
      setStaffReservations(data);
      setPendingStatusById(
        data.reduce((acc, item) => {
          acc[item._id] = toReservationState(item) || "pending";
          return acc;
        }, {})
      );
    } catch (error) {
      toast({
        title: "Failed to load customer reservations",
        description: error?.response?.data?.message || error.message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setLoadingStaffReservations(false);
    }
  };

  useEffect(() => {
    loadStaffReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const optionMappings = {
    1: { tableData: reservationData, title: "Reservation Blocks" },
  };
  const selectedMapping = optionMappings[selectedOption];

  const selectedDateKey = useMemo(() => {
    if (!(calendarDate instanceof Date)) {
      return "";
    }
    return toLocalDateKey(calendarDate);
  }, [calendarDate]);

  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i += 1) {
      const day = new Date(weekStart);
      day.setDate(weekStart.getDate() + i);
      days.push(day);
    }
    return days;
  }, [weekStart]);

  const reservationsForDay = useMemo(() => {
    if (!selectedDateKey) {
      return [];
    }
    const query = reservationSearch.trim().toLowerCase();
    return staffReservations.filter((reservation) => {
      const isSameDay =
        toLocalDateKey(reservation.reservationDate) === selectedDateKey;
      if (!isSameDay) {
        return false;
      }
      if (!query) {
        return true;
      }
      return toReservationCode(reservation).toLowerCase().includes(query);
    });
  }, [selectedDateKey, staffReservations, reservationSearch]);

  const reservationsForWeek = useMemo(() => {
    const start = new Date(weekStart);
    const end = new Date(weekStart);
    end.setDate(start.getDate() + 7);
    const query = reservationSearch.trim().toLowerCase();
    return staffReservations.filter(item => {
      const key = toLocalDateKey(item.reservationDate);
      if (!key) {
        return false;
      }
      const normalizedDate = new Date(`${key}T00:00:00`);
      const inWeek = normalizedDate >= start && normalizedDate < end;
      if (!inWeek) {
        return false;
      }
      if (!query) {
        return true;
      }
      return toReservationCode(item).toLowerCase().includes(query);
    });
  }, [staffReservations, weekStart, reservationSearch]);

  const handleUpdateStatus = async (reservationId, forcedStatus) => {
    const status = forcedStatus || pendingStatusById[reservationId];
    if (!status) {
      return;
    }

    setSavingReservationId(reservationId);
    try {
      const response = await updateStaffReservationStatus(reservationId, { status });
      const updated = response.data?.reservation;
      setStaffReservations(prev =>
        prev.map(item => (item._id === reservationId ? updated || item : item))
      );
      setPendingStatusById(prev => ({
        ...prev,
        [reservationId]: toReservationState(updated || { status }),
      }));
      toast({
        title: "Reservation updated",
        status: "success",
        duration: 2200,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Update failed",
        description: error?.response?.data?.message || error.message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setSavingReservationId("");
    }
  };

  const openEditReservation = reservation => {
    setEditingReservationId(reservation._id);
    setEditReservation({
      fullName: reservation.customer?.fullName || "",
      emailAddress: reservation.customer?.emailAddress || "",
      phoneNumber: reservation.customer?.phoneNumber || "",
      guests: String(reservation.guests || 2),
      date: String(reservation.reservationDate || "").slice(0, 10),
      time: reservation.reservationTime || "7:00 PM",
      vibe: reservation.vibe || "",
      status: reservation.status || "pending",
      adminNotes: reservation.adminNotes || "",
    });
    onEditOpen();
  };

  const closeEditReservation = (force = false) => {
    if (savingEditReservation && !force) {
      return;
    }
    setEditingReservationId("");
    onEditClose();
  };

  const handleSaveReservationEdit = async () => {
    if (!editingReservationId) {
      return;
    }
    if (
      !editReservation.fullName.trim() ||
      !editReservation.emailAddress.trim() ||
      !editReservation.phoneNumber.trim() ||
      !editReservation.guests.trim() ||
      !editReservation.date.trim() ||
      !editReservation.time.trim()
    ) {
      toast({
        title: "Missing fields",
        description: "Please complete all required reservation fields.",
        status: "warning",
        duration: 2600,
        isClosable: true,
      });
      return;
    }

    setSavingEditReservation(true);
    try {
      const response = await updateStaffReservation(editingReservationId, {
        fullName: editReservation.fullName.trim(),
        emailAddress: editReservation.emailAddress.trim().toLowerCase(),
        phoneNumber: editReservation.phoneNumber.trim(),
        guests: Number(editReservation.guests),
        date: editReservation.date.trim(),
        time: editReservation.time.trim(),
        vibe: editReservation.vibe.trim(),
        status: editReservation.status,
        adminNotes: editReservation.adminNotes.trim(),
      });
      const updated = response.data?.reservation;
      if (updated) {
        setStaffReservations(prev =>
          prev.map(item => (item._id === editingReservationId ? updated : item))
        );
        setPendingStatusById(prev => ({
          ...prev,
          [editingReservationId]: toReservationState(updated),
        }));
      }
      toast({
        title: "Reservation edited",
        description: "Changes saved and edit email sent.",
        status: "success",
        duration: 2200,
        isClosable: true,
      });
      closeEditReservation(true);
    } catch (error) {
      toast({
        title: "Save failed",
        description: error?.response?.data?.message || error.message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setSavingEditReservation(false);
    }
  };

  const handleDeleteReservation = async reservationId => {
    if (!reservationId) {
      return;
    }
    const confirmed = window.confirm(
      "Cancel this reservation? A cancellation email will be sent."
    );
    if (!confirmed) {
      return;
    }

    setDeletingReservationId(reservationId);
    try {
      const response = await deleteStaffReservation(reservationId);
      const cancelled = response.data?.reservation;
      setStaffReservations(prev =>
        prev.map(item => (item._id === reservationId ? cancelled || item : item))
      );
      setPendingStatusById(prev => ({
        ...prev,
        [reservationId]: "cancelled",
      }));
      if (editingReservationId === reservationId) {
        closeEditReservation(true);
      }
      toast({
        title: "Reservation cancelled",
        description: "Cancellation email sent.",
        status: "success",
        duration: 2200,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Delete failed",
        description: error?.response?.data?.message || error.message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setDeletingReservationId("");
    }
  };

  const handleCreateReservation = async () => {
    if (
      !newReservation.fullName.trim() ||
      !newReservation.emailAddress.trim() ||
      !newReservation.phoneNumber.trim() ||
      !newReservation.guests.trim() ||
      !newReservation.date.trim() ||
      !newReservation.time.trim()
    ) {
      toast({
        title: "Missing fields",
        description: "Please fill all required fields.",
        status: "warning",
        duration: 2600,
        isClosable: true,
      });
      return;
    }

    setCreatingReservation(true);
    try {
      const response = await createStaffReservation({
        fullName: newReservation.fullName.trim(),
        emailAddress: newReservation.emailAddress.trim().toLowerCase(),
        phoneNumber: newReservation.phoneNumber.trim(),
        guests: Number(newReservation.guests),
        time: newReservation.time.trim(),
        date: newReservation.date.trim(),
        vibe: newReservation.vibe.trim(),
      });

      const created = response.data?.reservation;
      if (created) {
        setStaffReservations(prev => [created, ...prev]);
        setPendingStatusById(prev => ({
          ...prev,
          [created._id]: toReservationState(created),
        }));
      }

      toast({
        title: "Reservation added",
        status: "success",
        duration: 2200,
        isClosable: true,
      });
      onClose();
    } catch (error) {
      toast({
        title: "Create failed",
        description: error?.response?.data?.message || error.message,
        status: "error",
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setCreatingReservation(false);
    }
  };

  const goToday = () => {
    const now = new Date();
    setCalendarDate(now);
    setWeekStart(startOfWeek(now));
  };

  const shiftWeek = direction => {
    setWeekStart(prev => {
      const next = new Date(prev);
      next.setDate(prev.getDate() + direction * 7);
      return next;
    });
  };

  const titleColor = useColorModeValue("navy.700", "white");
  const textColor = useColorModeValue("secondaryGray.700", "secondaryGray.300");
  const gridBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const gridMuted = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const cardBg = useColorModeValue("white", "navy.800");
  const whenBg = useColorModeValue("blue.50", "blue.900");
  const whenBorder = useColorModeValue("blue.200", "blue.500");

  const HOURS = Array.from({ length: 15 }).map((_, index) => 11 + index);
  const GRID_START_MIN = 11 * 60;
  // Keep minute range aligned with rendered row count so event positioning
  // lands in the correct visual hour cell.
  const GRID_END_MIN = GRID_START_MIN + HOURS.length * 60;
  const HOUR_HEIGHT = 56;
  const GRID_HEIGHT = HOURS.length * HOUR_HEIGHT;
  const SLOT_STACK_LIMIT = 3;
  const SLOT_STACK_GAP = 16;
  const SLOT_CARD_HEIGHT = 44;

  const calendarEvents = useMemo(() => {
    const bySlot = new Map();

    reservationsForWeek.forEach(reservation => {
      const reservationDateKey = toLocalDateKey(reservation.reservationDate);
      if (!reservationDateKey) {
        return;
      }
      const date = new Date(`${reservationDateKey}T00:00:00`);
      const dayIndex = Math.floor(
        (stripTime(date) - stripTime(weekStart)) / (24 * 60 * 60 * 1000)
      );
      if (dayIndex < 0 || dayIndex > 6) {
        return;
      }

      const rawMinutes = parseTimeToMinutes(reservation.reservationTime);
      const calendarMinutes = toCalendarMinutes(rawMinutes, GRID_START_MIN);
      const safeMinutes = Number.isFinite(calendarMinutes)
        ? calendarMinutes
        : GRID_START_MIN;
      const baseTop =
        ((safeMinutes - GRID_START_MIN) / (GRID_END_MIN - GRID_START_MIN)) *
        GRID_HEIGHT;
      const clampedTop = Math.max(0, Math.min(GRID_HEIGHT - SLOT_CARD_HEIGHT, baseTop));
      const slotMinuteKey = Math.round(safeMinutes);
      const slotKey = `${dayIndex}-${slotMinuteKey}`;

      if (!bySlot.has(slotKey)) {
        bySlot.set(slotKey, {
          dayIndex,
          clampedTop,
          reservations: [],
        });
      }
      bySlot.get(slotKey).reservations.push(reservation);
    });

    return Array.from(bySlot.values()).flatMap(slot => {
      const total = slot.reservations.length;
      const visibleCount = Math.min(SLOT_STACK_LIMIT, total);
      const hiddenCount = Math.max(0, total - SLOT_STACK_LIMIT);
      return slot.reservations.slice(0, visibleCount).map((reservation, index) => ({
        reservation,
        dayIndex: slot.dayIndex,
        top: Math.max(
          0,
          Math.min(
            GRID_HEIGHT - SLOT_CARD_HEIGHT,
            slot.clampedTop + index * SLOT_STACK_GAP
          )
        ),
        index,
        hiddenCount: index === visibleCount - 1 ? hiddenCount : 0,
      }));
    });
  }, [GRID_END_MIN, GRID_HEIGHT, GRID_START_MIN, reservationsForWeek, weekStart]);

  return (
    <Box pt="80px">
      <SimpleGrid
        mb="20px"
        columns={1}
        spacing="20px"
        width="100%"
      >
        {selectedMapping && reservationData && (
          <ReservationTable
            columnsData={columnsData}
            tableData={selectedMapping.tableData}
            title={selectedMapping.title}
            setSelectedOption={setSelectedOption}
            options={options}
          />
        )}

        <Card px="24px" py="20px">
          <Flex justify="space-between" align="center" mb="16px" wrap="wrap" gap="10px">
            <Box>
              <Text fontSize="xl" fontWeight="700" color={titleColor}>
                Customer Reservations Calendar
              </Text>
              <Text fontSize="sm" color={textColor}>
                Select a day to view reservations and update status.
              </Text>
            </Box>
            <HStack spacing="8px">
              <Button variant="outline" onClick={onOpen}>
                Add Reservation
              </Button>
              <Button colorScheme="blue" onClick={loadStaffReservations} isLoading={loadingStaffReservations}>
                Refresh
              </Button>
            </HStack>
          </Flex>

          <Box mb="18px" borderWidth="1px" borderColor={gridBorder} borderRadius="14px" overflow="hidden" bg={cardBg}>
            <Flex px="14px" py="10px" justify="space-between" align="center" borderBottomWidth="1px" borderColor={gridBorder} wrap="wrap" gap="8px">
              <HStack spacing="8px">
                <Button size="sm" onClick={goToday}>Today</Button>
                <Button size="sm" variant="ghost" onClick={() => shiftWeek(-1)}>&lt;</Button>
                <Button size="sm" variant="ghost" onClick={() => shiftWeek(1)}>&gt;</Button>
              </HStack>
              <Text fontWeight="700" color={titleColor}>
                {weekStart.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
              </Text>
            </Flex>

            <Flex px="10px" py="8px" borderBottomWidth="1px" borderColor={gridBorder}>
              <Box w="64px" />
              {weekDays.map(day => {
                const key = toLocalDateKey(day);
                const active = key === selectedDateKey;
                return (
                  <PressableDateHeader
                    key={key}
                    day={day}
                    active={active}
                    onPress={() => setCalendarDate(day)}
                    textColor={textColor}
                    titleColor={titleColor}
                  />
                );
              })}
            </Flex>

            <Flex>
              <Box w="64px" borderRightWidth="1px" borderColor={gridBorder}>
                {HOURS.map(hour => (
                  <Box key={`hour-${hour}`} h={`${HOUR_HEIGHT}px`} borderBottomWidth="1px" borderColor={gridBorder} px="6px" pt="2px">
                    <Text fontSize="11px" color={gridMuted}>
                      {formatHour(hour)}
                    </Text>
                  </Box>
                ))}
              </Box>

              <Box flex="1" position="relative" h={`${GRID_HEIGHT}px`}>
                {HOURS.map((hour, rowIndex) => (
                  <Flex key={`grid-row-${hour}`} h={`${HOUR_HEIGHT}px`} borderBottomWidth="1px" borderColor={gridBorder}>
                    {weekDays.map(day => (
                      <Box
                        key={`${day.toISOString()}-${hour}`}
                        flex="1"
                        borderRightWidth="1px"
                        borderColor={gridBorder}
                      />
                    ))}
                  </Flex>
                ))}

                {calendarEvents.map(event => {
                  const { reservation, dayIndex, top, hiddenCount } = event;
                  const leftPercent = (dayIndex / 7) * 100;
                  const widthPercent = 100 / 7;
                  const state = toReservationState(reservation);

                  return (
                    <Box
                      key={`calendar-${reservation._id}-${event.index}`}
                      position="absolute"
                      top={`${top}px`}
                      left={`${leftPercent}%`}
                      width={`calc(${widthPercent}% - 8px)`}
                      ml="4px"
                      mr="4px"
                      px="7px"
                      py="5px"
                      borderRadius="8px"
                      borderWidth="1px"
                      borderColor={
                        state === "confirmed"
                          ? "green.300"
                          : state === "cancelled"
                            ? "red.300"
                            : "yellow.300"
                      }
                      bg={
                        state === "confirmed"
                          ? "green.50"
                          : state === "cancelled"
                            ? "red.50"
                            : "yellow.50"
                      }
                      cursor="pointer"
                      onClick={() => openEditReservation(reservation)}
                    >
                      <Text fontSize="10px" fontWeight="700" noOfLines={1}>
                        {reservation.customer?.fullName || "Guest"} ({reservation.guests})
                      </Text>
                      <Text fontSize="10px" color="gray.600" noOfLines={1}>
                        {reservation.reservationTime}
                      </Text>
                      {hiddenCount > 0 ? (
                        <Text fontSize="10px" fontWeight="700" color="gray.700" noOfLines={1}>
                          +{hiddenCount} more
                        </Text>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            </Flex>
          </Box>

          <Box mb="12px">
            <Input
              value={reservationSearch}
              onChange={e => setReservationSearch(e.target.value)}
              placeholder="Search by reservation ID"
              maxW="360px"
            />
          </Box>

          {!reservationsForDay.length ? (
            <Text color={textColor}>No customer reservations for this date.</Text>
          ) : (
            <Stack spacing="12px">
              {reservationsForDay.map(reservation => (
                <Box
                  key={reservation._id}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="12px"
                  p="12px"
                  onClick={() => openEditReservation(reservation)}
                  cursor="pointer"
                >
                  <Flex justify="space-between" align="center" wrap="wrap" gap="8px" mb="8px">
                    <Text fontWeight="800" color={titleColor}>
                      {reservation.customer?.fullName || "Guest"}
                    </Text>
                    <Badge colorScheme={STATUS_COLOR[toReservationState(reservation)] || "gray"}>
                      {toReservationStatusLabel(toReservationState(reservation))}
                    </Badge>
                  </Flex>
                  <Text fontSize="sm" color={textColor}>
                    {reservation.customer?.phoneNumber || "-"} • {reservation.customer?.emailAddress || "-"}
                  </Text>
                  <Text fontSize="sm" color={textColor}>
                    Guests: {reservation.guests} • Time: {reservation.reservationTime}
                  </Text>
                  <Text fontSize="sm" fontWeight="700" color={titleColor}>
                    ID: {toReservationCode(reservation)}
                  </Text>

                  <HStack mt="10px" spacing="8px" align="center" wrap="wrap">
                    <Select
                      size="sm"
                      w="200px"
                      value={pendingStatusById[reservation._id] || reservation.status}
                      onClick={e => e.stopPropagation()}
                      onChange={e =>
                        setPendingStatusById(prev => ({
                          ...prev,
                          [reservation._id]: e.target.value,
                        }))
                      }
                    >
                      {STAFF_STATUS_OPTIONS.map(status => (
                        <option key={status} value={status}>
                          {toReservationStatusLabel(status)}
                        </option>
                      ))}
                    </Select>
                    <Button
                      size="sm"
                      colorScheme="green"
                      onClick={e => {
                        e.stopPropagation();
                        handleUpdateStatus(reservation._id);
                      }}
                      isLoading={savingReservationId === reservation._id}
                    >
                      Update
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="teal"
                      onClick={e => {
                        e.stopPropagation();
                        handleUpdateStatus(reservation._id, "confirmed");
                      }}
                      isLoading={savingReservationId === reservation._id}
                    >
                      Confirmed
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={e => {
                        e.stopPropagation();
                        handleUpdateStatus(reservation._id, "seated");
                      }}
                      isLoading={savingReservationId === reservation._id}
                    >
                      Seated
                    </Button>
                    {toReservationState(reservation) !== "cancelled" ? (
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={e => {
                          e.stopPropagation();
                          handleDeleteReservation(reservation._id);
                        }}
                        isLoading={deletingReservationId === reservation._id}
                      >
                        Cancel
                      </Button>
                    ) : null}
                  </HStack>
                </Box>
              ))}
            </Stack>
          )}
        </Card>
      </SimpleGrid>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Reservation</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="18px">
            <Stack spacing="10px">
              <Box
                p="12px"
                borderWidth="1px"
                borderColor={whenBorder}
                bg={whenBg}
                borderRadius="12px"
              >
                <Text fontSize="sm" fontWeight="800" mb="8px">
                  When
                </Text>
                <SimpleGrid columns={2} spacing="10px">
                  <Box>
                    <Text fontSize="xs" mb="4px" color={textColor}>
                      Date
                    </Text>
                    <Input
                      type="date"
                      size="lg"
                      value={newReservation.date}
                      onChange={e =>
                        setNewReservation(prev => ({ ...prev, date: e.target.value }))
                      }
                      bg={cardBg}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="xs" mb="4px" color={textColor}>
                      Time
                    </Text>
                    <Input
                      type="time"
                      size="lg"
                      value={normalizeTimeForInput(newReservation.time)}
                      onChange={e =>
                        setNewReservation(prev => ({
                          ...prev,
                          time: normalizeTimeForDisplay(e.target.value),
                        }))
                      }
                      bg={cardBg}
                    />
                  </Box>
                </SimpleGrid>
              </Box>

              <Input
                placeholder="Full name"
                value={newReservation.fullName}
                onChange={e => setNewReservation(prev => ({ ...prev, fullName: e.target.value }))}
              />
              <Input
                placeholder="Email"
                value={newReservation.emailAddress}
                onChange={e => setNewReservation(prev => ({ ...prev, emailAddress: e.target.value }))}
              />
              <Input
                placeholder="Phone number"
                value={newReservation.phoneNumber}
                onChange={e => setNewReservation(prev => ({ ...prev, phoneNumber: e.target.value }))}
              />
              <Input
                placeholder="Guests"
                type="number"
                value={newReservation.guests}
                onChange={e => setNewReservation(prev => ({ ...prev, guests: e.target.value }))}
              />
              <Input
                placeholder="Vibe (optional)"
                value={newReservation.vibe}
                onChange={e => setNewReservation(prev => ({ ...prev, vibe: e.target.value }))}
              />
              <Button colorScheme="blue" onClick={handleCreateReservation} isLoading={creatingReservation}>
                Save Reservation
              </Button>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isEditOpen} onClose={closeEditReservation} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Reservation</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="18px">
            <Stack spacing="10px">
              <SimpleGrid columns={2} spacing="10px">
                <Box>
                  <Text fontSize="xs" mb="4px" color={textColor}>
                    Date
                  </Text>
                  <Input
                    type="date"
                    value={editReservation.date}
                    onChange={e =>
                      setEditReservation(prev => ({ ...prev, date: e.target.value }))
                    }
                    bg={cardBg}
                  />
                </Box>
                <Box>
                  <Text fontSize="xs" mb="4px" color={textColor}>
                    Time
                  </Text>
                  <Input
                    type="time"
                    value={normalizeTimeForInput(editReservation.time)}
                    onChange={e =>
                      setEditReservation(prev => ({
                        ...prev,
                        time: normalizeTimeForDisplay(e.target.value),
                      }))
                    }
                    bg={cardBg}
                  />
                </Box>
              </SimpleGrid>

              <Input
                placeholder="Full name"
                value={editReservation.fullName}
                onChange={e =>
                  setEditReservation(prev => ({ ...prev, fullName: e.target.value }))
                }
              />
              <Input
                placeholder="Email"
                value={editReservation.emailAddress}
                onChange={e =>
                  setEditReservation(prev => ({ ...prev, emailAddress: e.target.value }))
                }
              />
              <Input
                placeholder="Phone number"
                value={editReservation.phoneNumber}
                onChange={e =>
                  setEditReservation(prev => ({ ...prev, phoneNumber: e.target.value }))
                }
              />
              <Input
                placeholder="Guests"
                type="number"
                value={editReservation.guests}
                onChange={e =>
                  setEditReservation(prev => ({ ...prev, guests: e.target.value }))
                }
              />
              <Input
                placeholder="Vibe"
                value={editReservation.vibe}
                onChange={e =>
                  setEditReservation(prev => ({ ...prev, vibe: e.target.value }))
                }
              />
              <Select
                value={editReservation.status}
                onChange={e =>
                  setEditReservation(prev => ({ ...prev, status: e.target.value }))
                }
              >
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="seated">Seated</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </Select>
              <Textarea
                placeholder="Admin notes"
                value={editReservation.adminNotes}
                onChange={e =>
                  setEditReservation(prev => ({ ...prev, adminNotes: e.target.value }))
                }
              />
              <Button
                colorScheme="blue"
                onClick={handleSaveReservationEdit}
                isLoading={savingEditReservation}
              >
                Save Changes
              </Button>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function startOfWeek(date) {
  const cloned = new Date(date);
  const day = cloned.getDay();
  cloned.setHours(0, 0, 0, 0);
  cloned.setDate(cloned.getDate() - day);
  return cloned;
}

function stripTime(date) {
  const cloned = new Date(date);
  cloned.setHours(0, 0, 0, 0);
  return cloned.getTime();
}

function toLocalDateKey(value) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length >= 10) {
      return trimmed.slice(0, 10);
    }
  }
  const date = value instanceof Date ? value : new Date(String(value || ""));
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseTimeToMinutes(value) {
  const input = String(value || "").trim().toUpperCase();
  if (!input) {
    return NaN;
  }
  const twelveHourMatch = input.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/);
  if (twelveHourMatch) {
    let hour = Number(twelveHourMatch[1]);
    const minutes = Number(twelveHourMatch[2] || "0");
    const meridiem = twelveHourMatch[3];
    if (meridiem === "PM" && hour < 12) {
      hour += 12;
    }
    if (meridiem === "AM" && hour === 12) {
      hour = 0;
    }
    return hour * 60 + minutes;
  }

  const twentyFourHourMatch = input.match(/^(\d{1,2}):(\d{2})$/);
  if (twentyFourHourMatch) {
    const hour = Number(twentyFourHourMatch[1]);
    const minutes = Number(twentyFourHourMatch[2]);
    if (
      Number.isFinite(hour) &&
      Number.isFinite(minutes) &&
      hour >= 0 &&
      hour <= 23 &&
      minutes >= 0 &&
      minutes <= 59
    ) {
      return hour * 60 + minutes;
    }
  }

  return NaN;
}

function toCalendarMinutes(rawMinutes, gridStartMinutes) {
  if (!Number.isFinite(rawMinutes)) {
    return NaN;
  }
  return rawMinutes < gridStartMinutes ? rawMinutes + 24 * 60 : rawMinutes;
}

function formatHour(hour24) {
  const normalized = ((hour24 % 24) + 24) % 24;
  const suffix = normalized >= 12 ? "PM" : "AM";
  const hour12 = normalized % 12 === 0 ? 12 : normalized % 12;
  return `${hour12} ${suffix}`;
}

function normalizeTimeForInput(displayTime) {
  const totalMinutes = parseTimeToMinutes(displayTime);
  if (!Number.isFinite(totalMinutes)) {
    return "19:00";
  }
  const hour = Math.max(0, Math.min(23, Math.floor(totalMinutes / 60)));
  const minutes = Math.max(0, Math.min(59, totalMinutes % 60));
  return `${String(hour).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function normalizeTimeForDisplay(timeValue) {
  const [hoursRaw, minutesRaw] = String(timeValue || "").split(":");
  const hours24 = Number(hoursRaw);
  const minutes = Number(minutesRaw || "0");
  if (!Number.isFinite(hours24) || !Number.isFinite(minutes)) {
    return "7:00 PM";
  }
  const suffix = hours24 >= 12 ? "PM" : "AM";
  const hour12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hour12}:${String(minutes).padStart(2, "0")} ${suffix}`;
}

function PressableDateHeader({ day, active, onPress, textColor, titleColor }) {
  return (
    <Box flex="1" px="8px">
      <Button
        variant={active ? "solid" : "ghost"}
        size="sm"
        colorScheme={active ? "blue" : "gray"}
        onClick={onPress}
        w="100%"
      >
        <Stack spacing="0px">
          <Text fontSize="10px" color={active ? "white" : textColor}>
            {day.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase()}
          </Text>
          <Text fontSize="13px" fontWeight="700" color={active ? "white" : titleColor}>
            {day.getDate()}
          </Text>
        </Stack>
      </Button>
    </Box>
  );
}
